import React, { useState, useEffect } from 'react';
import { ordersAPI } from '../../services/api';
import './OrdersPage.css';

interface Order {
  id: number;
  orderNumber: string;
  merchantOrderNumber: string;
  courseName: string;
  paymentAmount: number;
  paymentTime: string;
  status: string;
  gradeLevel: string;
  semester: string;
  location: string;
  teacherName: string;
  imageUrl?: string;
}

const OrdersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('0');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  const tabs = [
    { key: '0', label: '全部' },
    { key: '1', label: '已付款' },
    { key: '2', label: '已完成' },
    { key: '3', label: '已取消' },
    { key: '4', label: '已退款' }
  ];

  useEffect(() => {
    fetchOrders();
  }, [activeTab]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getMyOrders();
      if (Array.isArray(response)) {
        setOrders(response);
      } else if (response && typeof response === 'object' && 'orders' in response) {
        setOrders((response as { orders: Order[] }).orders || []);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('获取订单失败:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const mapStatusToChinese = (englishStatus: string) => {
    const statusMap: { [key: string]: string } = {
      'pending': '待付款',
      'paid': '已付款',
      'completed': '已完成',
      'cancelled': '已取消',
      'refunded': '已退款'
    };
    return statusMap[englishStatus] || englishStatus;
  };

  const getStatusClass = (status: string) => {
    const classMap: { [key: string]: string } = {
      '待付款': 'status-pending',
      '已付款': 'status-paid',
      '已完成': 'status-completed',
      '已取消': 'status-cancelled',
      '已退款': 'status-refunded'
    };
    return classMap[status] || '';
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '时间待定';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount: number) => {
    return `¥${(amount || 0).toFixed(2)}`;
  };

  const filteredOrders = orders.filter(order => {
    if (activeTab === '0') return true;
    const statusMap: { [key: string]: string } = {
      '1': '已付款',
      '2': '已完成',
      '3': '已取消',
      '4': '已退款'
    };
    return order.status === statusMap[activeTab];
  });

  if (loading) {
    return (
      <div className="orders-page">
        <div className="orders-header">
          <h1>我的订单</h1>
        </div>
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-header">
        <div className="header-content">
          <h1>我的订单</h1>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs-wrapper">
          <ul className="tabs-list">
            {tabs.map(tab => (
              <li
                key={tab.key}
                className={`tab-item ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span>{tab.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="orders-content">
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-box">
              <div className="nodata">
                <img 
                  src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSJ3aGl0ZSIvPgo8Y2lyY2xlIGN4PSI2MCIgY3k9IjQwIiByPSIyNCIgZmlsbD0iI0Y1RjVGNSIvPgo8Y2lyY2xlIGN4PSI2MCIgY3k9IjQwIiByPSIxMiIgZmlsbD0iI0U2RTZFNiIvPgo8cGF0aCBkPSJNMzAgOTBDMzAgODMuMzcyOCAzNC4zNzI4IDc5IDQxIDc5SDc5Qzg1LjYyNzIgNzkgOTAgODMuMzcyOCA5MCA5MFY5NUgzMFY5MFoiIGZpbGw9IiNGNUY1RjUiLz4KPHBhdGggZD0iTTQyIDU1QzQyIDUxLjY4NjMgNDQuNjg2MyA0OSA0OCA0OUM1MS4zMTM3IDQ5IDU0IDUxLjY4NjMgNTQgNTVDNTQgNTguMzEzNyA1MS4zMTM3IDYxIDQ4IDYxQzQ0LjY4NjMgNjEgNDIgNTguMzEzNyA0MiA1NVoiIGZpbGw9IiNFOUU5RTkiLz4KPHBhdGggZD0iTTY2IDU1QzY2IDUxLjY4NjMgNjguNjg2MyA0OSA3MiA0OUM3NS4zMTM3IDQ5IDc4IDUxLjY4NjMgNzggNTVDNzggNTguMzEzNyA3NS4zMTM3IDYxIDcyIDYxQzY4LjY4NjMgNjEgNjYgNTguMzEzNyA2NiA1NVoiIGZpbGw9IiNFOUU5RTkiLz4KPC9zdmc+"
                  alt="Empty"
                  className="empty-image"
                />
                <p>暂无订单信息</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="orders-list">
            {filteredOrders.map(order => (
              <div key={order.id} className="order-card">
                <div className="order-header">
                  <h3 className="order-title">
                    <em className="course-name">{order.courseName}</em>
                    <span className={`order-status ${getStatusClass(order.status)}`}>
                      {order.status}
                    </span>
                  </h3>
                </div>

                <div className="order-details">
                  <div className="detail-item">
                    <span className="label">订单金额：</span>
                    <span className="content amount">{formatAmount(order.paymentAmount)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">交易订单号：</span>
                    <span className="content order-number">{order.orderNumber}</span>
                  </div>
                  {order.merchantOrderNumber && (
                    <div className="detail-item">
                      <span className="label">支付订单号：</span>
                      <span className="content merchant-number">{order.merchantOrderNumber}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="label">付款时间：</span>
                    <span className="content payment-time">{formatDateTime(order.paymentTime)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">付款状态：</span>
                    <span className={`content ${getStatusClass(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                  {order.teacherName && (
                    <div className="detail-item">
                      <span className="label">授课老师：</span>
                      <span className="content">{order.teacherName}</span>
                    </div>
                  )}
                  {order.location && (
                    <div className="detail-item">
                      <span className="label">上课地点：</span>
                      <span className="content">{order.location}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div className="list-finished-text">没有更多了</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;